import type { InstitutionalProfileFormData } from '@/types/institutionalProfile'
import { FormField, SectionHeading } from './FormField'
import type { SectionDef } from './institutionalFields'

interface ProfileStepProps {
  /**
   * Which sections to render — `PHASE_ONE_SECTIONS` for the door,
   * `PHASE_TWO_SECTIONS` for the rest. Passed in rather than chosen here so
   * one component serves both phases and the phase split stays in exactly one
   * place (`institutionalFields.ts`).
   */
  sections: SectionDef[]
  profile: InstitutionalProfileFormData
  onChange: (name: keyof InstitutionalProfileFormData, value: string | boolean) => void
  errors: Record<string, string>
  /** Write-only fields the server already holds a value for. */
  onFile: Record<string, boolean>
  disabled?: boolean
}

/**
 * The descriptive half of the institutional profile, one phase at a time.
 *
 * Rendered entirely from the section schema; adding a field is a one-line edit
 * to `institutionalFields.ts`, not a change here.
 */
export const ProfileStep = ({
  sections,
  profile,
  onChange,
  errors,
  onFile,
  disabled,
}: ProfileStepProps) => (
  <div className="space-y-10">
    {sections.map(section => (
      <section key={section.id}>
        <SectionHeading title={section.title} description={section.description} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {section.fields
            .filter(
              field =>
                !field.showWhen ||
                field.showWhen.equalsAny.includes(String(profile[field.showWhen.field])),
            )
            .map(field => (
              <FormField
                key={field.name}
                field={field}
                value={profile[field.name]}
                onChange={value => onChange(field.name, value)}
                error={errors[field.name]}
                onFile={field.writeOnly ? onFile[field.name] : undefined}
                disabled={disabled}
              />
            ))}
        </div>
      </section>
    ))}
  </div>
)
